import { nonNullable, sortByAll, uniqueBy } from 'lib/array/arrayUtils'
import { getOfferName, getOfferPropertyName, getPackageUniqueKey } from 'lib/offer/offerUtils'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import { reportLog } from 'services/userMonitoring'
import {
  formatAssistantRoomLabel,
  formatAssistantRoomOccupancyText,
  formatAssistantRoomsOccupancyText,
} from 'tripPlanner/assistant/utils/assistantRoomOccupancyText'
import { getAssistantOccupancyAgeThresholdsFromSnapshotSource } from 'tripPlanner/assistant/utils/assistantOccupancyAgeThresholds'
import { assistantSurfaceSnapshotChunkContentHash } from 'tripPlanner/assistant/surfaceSnapshots/fingerprint'
import type {
  AssistantSurfaceSnapshotChunk,
  AssistantSurfaceSnapshotChunkManifest,
  AssistantSurfaceSnapshotChunkRef,
  AssistantSurfaceSnapshotRequest,
  SurfaceAtomRequest,
  SurfaceCapabilityRequest,
  SurfaceManifestFactRequest,
  SurfaceRecentCommittedStateRequest,
  SurfaceTargetRequest,
} from 'tripPlanner/assistant/api/surfaceSnapshotContract'
import type {
  OfferPageAssistantSource,
  OfferPageSnapshotContribution,
  OfferPageSnapshotInput,
  OfferPageSnapshotRoom,
  OfferPageSnapshotSelection,
} from './offerPageSnapshotTypes'
import {
  buildOfferPageSnapshotScopeKeyFromSource,
} from './offerPageSnapshotIdentity'
import {
  formatFacilityGroupsText,
  getPropertyFacilitiesSnapshotText,
  splitOfferPageSnapshotAtomText,
  toOfferPageSnapshotAtomText,
  toOfferPageSnapshotId,
  toOfferPageSnapshotLabel,
  toOfferPageSnapshotManifestFactText,
  toOfferPageSnapshotSourcePath,
  toOfferPageSnapshotTargetText,
} from './offerPageSnapshotText'
import { buildOfferPageFinePrintSectionsFromSources } from './offerPageFinePrintContext'
import { nonEmptyString } from './offerPageContextFormatting'

const sortAtoms = (atoms: Array<SurfaceAtomRequest>) => sortByAll(atoms, [
  { selector: atom => atom.rankHint ?? 0, direction: 'asc' },
  { selector: atom => atom.sourcePath ?? '', direction: 'asc' },
  { selector: atom => atom.atomId ?? '', direction: 'asc' },
])

const sortTargets = (targets: Array<SurfaceTargetRequest>) => sortByAll(targets, [
  { selector: target => target.sourcePath ?? '', direction: 'asc' },
  { selector: target => target.targetId ?? '', direction: 'asc' },
])

const sortCommittedState = (committed: Array<SurfaceRecentCommittedStateRequest>) => sortByAll(committed, [
  { selector: state => state.expiresAt ?? '', direction: 'desc' },
  { selector: state => state.kind ?? '', direction: 'asc' },
  { selector: state => state.key ?? '', direction: 'asc' },
])

const sortManifestFacts = (facts: Array<SurfaceManifestFactRequest>) => sortByAll(facts, [
  { selector: fact => fact.key ?? '', direction: 'asc' },
  { selector: fact => fact.value ?? '', direction: 'asc' },
])

export interface OfferPageSnapshotBuildResult {
  chunks: Array<AssistantSurfaceSnapshotChunk>
  manifest: AssistantSurfaceSnapshotChunkManifest
  snapshot: AssistantSurfaceSnapshotRequest
}

type OfferChunkId =
  | 'committed'
  | 'booking'
  | 'packages'
  | 'policies'
  | 'property'
  | 'reviews'
  | 'offer'

const capabilities: Array<SurfaceCapabilityRequest> = [{
  capability: 'surface_context.read',
  mode: 'available',
}]

function createFact(
  key: string,
  value: string | undefined,
  dataClassification: SurfaceManifestFactRequest['dataClassification'] = 'public_surface',
): SurfaceManifestFactRequest | undefined {
  const text = toOfferPageSnapshotManifestFactText(value)
  const factKey = toOfferPageSnapshotLabel(key)
  return text && factKey ? { key: factKey, value: text, dataClassification } : undefined
}

function createTarget(
  targetId: string,
  kind: string,
  label: string,
  sourcePath: string,
  options: {
    dataClassification?: SurfaceTargetRequest['dataClassification']
    linkStability?: SurfaceTargetRequest['linkStability']
    modelUse?: SurfaceTargetRequest['modelUse']
    parentTargetId?: string
  } = {},
): SurfaceTargetRequest {
  const normalizedLabel = toOfferPageSnapshotLabel(label)
  const normalizedSourcePath = toOfferPageSnapshotSourcePath(sourcePath)
  const displayText = toOfferPageSnapshotTargetText(label) ?? normalizedLabel
  return {
    targetId,
    kind: toOfferPageSnapshotLabel(kind) ?? kind,
    label: normalizedLabel ?? label,
    displayText: displayText ?? normalizedLabel ?? label,
    sourcePath: normalizedSourcePath ?? sourcePath,
    dataClassification: options.dataClassification ?? 'public_surface',
    availability: 'rendered',
    modelUse: options.modelUse ?? 'inline_link_only',
    linkStability: options.linkStability ?? 'exact_fingerprint',
    ...(options.parentTargetId ? { parentTargetId: options.parentTargetId } : {}),
  }
}

function createAtom(
  atomId: string,
  kind: string,
  label: string,
  displayText: string | undefined,
  sourcePath: string,
  targetId: string,
  rankHint: number,
  dataClassification: SurfaceAtomRequest['dataClassification'] = 'public_surface',
): SurfaceAtomRequest | undefined {
  const text = toOfferPageSnapshotAtomText(displayText)
  const normalizedLabel = toOfferPageSnapshotLabel(label)
  const normalizedSourcePath = toOfferPageSnapshotSourcePath(sourcePath)
  const normalizedKind = toOfferPageSnapshotLabel(kind)
  return text && normalizedLabel && normalizedSourcePath && normalizedKind ? {
    atomId,
    kind: normalizedKind,
    label: normalizedLabel,
    displayText: text,
    sourcePath: normalizedSourcePath,
    dataClassification,
    visibility: 'visible',
    modelUse: 'read_tool_only',
    targetId,
    rankHint,
  } : undefined
}

function sourceDisplayName(source: OfferPageAssistantSource): string | undefined {
  switch (source.variant) {
    case 'flash':
      return nonEmptyString(getOfferPropertyName(source.offer)) ?? nonEmptyString(getOfferName(source.offer))
    case 'lme_property':
    case 'spp_property':
      return nonEmptyString(source.property.name)
    case 'bedbank':
      return nonEmptyString(source.offer.property.name) ?? nonEmptyString(getOfferName(source.offer))
    default:
      return assertUnreachable(source)
  }
}

function sourceLocation(source: OfferPageAssistantSource): string | undefined {
  switch (source.variant) {
    case 'flash':
      return source.offer.location
    case 'lme_property':
    case 'spp_property':
      return source.property.location.title ?? source.property.location.formattedAddress
    case 'bedbank':
      return source.offer.property.location
    default:
      return assertUnreachable(source)
  }
}

function selectedDates(selection: OfferPageSnapshotSelection): string | undefined {
  const values = [selection.checkInDate, selection.checkOutDate].filter(Boolean)
  return values.length ? values.join(' to ') : undefined
}

function selectedGuests(
  selection: OfferPageSnapshotSelection,
  ageThresholds: ReturnType<typeof getAssistantOccupancyAgeThresholdsFromSnapshotSource>,
): string | undefined {
  return formatAssistantRoomsOccupancyText(selection.rooms, { ageThresholds })
}

function selectionSnapshot(
  selection: OfferPageSnapshotSelection,
  ageThresholds: ReturnType<typeof getAssistantOccupancyAgeThresholdsFromSnapshotSource>,
): {
  atoms: Array<SurfaceAtomRequest>
  committed: Array<SurfaceRecentCommittedStateRequest>
  facts: Array<SurfaceManifestFactRequest>
  targets: Array<SurfaceTargetRequest>
} {
  const targets = [
    createTarget('booking:dates', 'booking_control', 'Dates', 'offer.booking.dates', {
      dataClassification: 'personalised_surface',
    }),
    createTarget('booking:guests', 'booking_control', 'Guests', 'offer.booking.guests', {
      dataClassification: 'personalised_surface',
    }),
  ]
  const dates = selectedDates(selection)
  const guests = selectedGuests(selection, ageThresholds)
  const multiRoomModeText = selection.rooms.length > 1
    ? (selection.multiRoomMode === 'individual' ? 'Booking each room individually' : 'Same room and rate for every room')
    : undefined
  const atoms = nonNullable([
    createAtom('booking:dates', 'booking_selection', 'Selected dates', dates, 'offer.booking.dates', 'booking:dates', 10, 'personalised_surface'),
    createAtom('booking:guests', 'booking_selection', 'Selected guests', guests, 'offer.booking.guests', 'booking:guests', 20, 'personalised_surface'),
    createAtom('booking:flight-mode', 'booking_selection', 'Flight selection', selection.flights ? 'Flights included' : 'Hotel only', 'offer.booking.flight-mode', 'booking:dates', 30, 'personalised_surface'),
    createAtom('booking:rooms:mode', 'booking_selection', 'Room booking mode', multiRoomModeText, 'offer.booking.rooms.mode', 'booking:guests', 40, 'personalised_surface'),
  ])
  const facts = nonNullable([
    createFact('booking.duration_nights', selection.duration ? `${selection.duration}` : undefined, 'personalised_surface'),
    createFact('booking.price_mode', selection.priceMode, 'personalised_surface'),
    createFact('booking.fare_class', selection.fareClass, 'personalised_surface'),
  ])
  const committed: Array<SurfaceRecentCommittedStateRequest> = []
  if (dates) {
    committed.push({
      key: 'booking:dates',
      kind: 'booking_selection',
      label: 'Selected dates',
      displayText: toOfferPageSnapshotAtomText(dates) ?? dates,
      sourcePath: 'offer.booking.dates',
      dataClassification: 'personalised_surface',
      targetId: 'booking:dates',
    })
  }
  if (guests) {
    committed.push({
      key: 'booking:guests',
      kind: 'booking_selection',
      label: 'Selected guests',
      displayText: toOfferPageSnapshotAtomText(guests) ?? guests,
      sourcePath: 'offer.booking.guests',
      dataClassification: 'personalised_surface',
      targetId: 'booking:guests',
    })
  }

  return { atoms, committed, facts, targets }
}

function overviewSnapshot(source: OfferPageAssistantSource): {
  atoms: Array<SurfaceAtomRequest>
  facts: Array<SurfaceManifestFactRequest>
  targets: Array<SurfaceTargetRequest>
} {
  const displayName = sourceDisplayName(source)
  const location = sourceLocation(source)
  const targets = [
    createTarget('page:overview', 'section', 'Overview', 'offer.overview', {
      linkStability: 'same_scope_current_target',
      modelUse: 'auto_focus_allowed',
    }),
    createTarget('location:root', 'section', 'Location', 'offer.location', {
      linkStability: 'same_scope_current_target',
      modelUse: 'auto_focus_allowed',
    }),
  ]
  const atoms = nonNullable([
    createAtom('page:overview:identity', 'property_identity', 'Property', displayName, 'offer.overview.identity', 'page:overview', 10),
    createAtom('location:root:summary', 'location', 'Location', location, 'offer.location.summary', 'location:root', 20),
  ])
  const facts = nonNullable([
    createFact('offer.variant', source.variant),
    createFact('displayName', toOfferPageSnapshotLabel(displayName)),
    createFact('offer.display_name', displayName),
    createFact('offer.location', location),
  ])

  return { atoms, facts, targets }
}

function addTextAtom(
  atoms: Array<SurfaceAtomRequest>,
  atomId: string,
  kind: string,
  label: string,
  displayText: string | undefined,
  sourcePath: string,
  targetId: string,
  rankHint: number,
  dataClassification: SurfaceAtomRequest['dataClassification'] = 'public_surface',
): void {
  const segments = splitOfferPageSnapshotAtomText(displayText)
  segments.forEach((segment, index) => {
    const segmentSuffix = segments.length > 1 ? `:seg-${index + 1}` : ''
    const atom = createAtom(
      `${atomId}${segmentSuffix}`,
      kind,
      label,
      segment,
      segments.length > 1 ? `${sourcePath}${segmentSuffix}` : sourcePath,
      targetId,
      rankHint + index,
      dataClassification,
    )
    if (atom) {
      atoms.push(atom)
    }
  })
}

function staticContentSnapshot(source: OfferPageAssistantSource): {
  atoms: Array<SurfaceAtomRequest>
  targets: Array<SurfaceTargetRequest>
} {
  const atoms: Array<SurfaceAtomRequest> = []
  const targets: Array<SurfaceTargetRequest> = []
  const addSectionTarget = (
    targetId: string,
    label: string,
    sourcePath: string,
    dataClassification: SurfaceTargetRequest['dataClassification'] = 'public_surface',
  ) => {
    targets.push(createTarget(targetId, 'section', label, sourcePath, {
      dataClassification,
      linkStability: 'same_scope_current_target',
      modelUse: 'auto_focus_allowed',
    }))
  }

  switch (source.variant) {
    case 'flash':
      addSectionTarget('page:highlights', 'Highlights', 'offer.property.highlights')
      addSectionTarget('facilities:root', 'Facilities', 'offer.property.facilities')
      addSectionTarget('fine-print:root', 'Things to know', 'offer.policies')
      addTextAtom(atoms, 'page:overview:description', 'description', 'About this offer', source.offer.description, 'offer.overview.description', 'page:overview', 30)
      addTextAtom(atoms, 'page:highlights:content', 'highlights', 'Highlights', source.offer.highlights, 'offer.property.highlights', 'page:highlights', 40)
      addTextAtom(atoms, 'page:highlights:what-we-like', 'highlights', 'What we like', source.offer.whatWeLike, 'offer.property.what-we-like', 'page:highlights', 50)
      // Prefers structuredFacilities (the croFacilitiesRework experiment's data source) over the
      // legacy `facilities` string when present -- the experiment's own inView/bucketing gate is a
      // client rendering concern, not a signal of whether this content exists on the page.
      addTextAtom(atoms, 'facilities:root:content', 'facilities', 'Facilities', source.offer.structuredFacilities?.length
        ? formatFacilityGroupsText(source.offer.structuredFacilities.map(group => ({ name: group.group.name, itemNames: group.facilities.map(facility => facility.name) })))
        : source.offer.facilities, 'offer.property.facilities', 'facilities:root', 60)
      addTextAtom(atoms, 'location:root:getting-there', 'location', 'Getting there', source.offer.gettingThere, 'offer.location.getting-there', 'location:root', 70)
      break
    case 'bedbank':
      addSectionTarget('facilities:root', 'Facilities', 'offer.property.facilities')
      addSectionTarget('fine-print:root', 'Things to know', 'offer.policies')
      addTextAtom(atoms, 'page:overview:description', 'description', 'About this offer', source.offer.description, 'offer.overview.description', 'page:overview', 30)
      addTextAtom(atoms, 'page:overview:tagline', 'tagline', 'Offer details', source.offer.tagline, 'offer.overview.tagline', 'page:overview', 40)
      addTextAtom(atoms, 'page:overview:promotion', 'promotion', 'Promotion details', source.offer.additionalPromoContent, 'offer.overview.promotion', 'page:overview', 50, 'commercial_surface')
      // `offer.amenities` is a legacy summary string used by offer list/card tiles, not by
      // BedbankNavigableOfferContent's own Facilities accordion (which gates/renders from
      // `facilityGroups`/`property.facilitiesOverride` instead) -- read the same fields it does.
      addTextAtom(atoms, 'facilities:root:amenities', 'facilities', 'Facilities', source.offer.property?.facilitiesOverride
      ?? formatFacilityGroupsText(source.offer.facilityGroups.map(group => ({ name: group.name, itemNames: group.values.map(value => value.name) }))), 'offer.property.facilities', 'facilities:root', 60)
      addTextAtom(atoms, 'location:root:attractions', 'location', 'Nearby attractions', source.offer.attractions, 'offer.location.attractions', 'location:root', 70)
      break
    case 'lme_property':
    case 'spp_property':
      addSectionTarget('page:highlights', 'Highlights', 'offer.property.highlights')
      addSectionTarget('facilities:root', 'Facilities', 'offer.property.facilities')
      addSectionTarget('fine-print:root', 'Things to know', 'offer.policies')
      addTextAtom(atoms, 'page:overview:headline', 'headline', 'Property headline', source.property.headline, 'offer.overview.headline', 'page:overview', 30)
      addTextAtom(atoms, 'page:overview:description', 'description', 'About this property', source.property.description, 'offer.overview.description', 'page:overview', 40)
      addTextAtom(atoms, 'page:overview:additional-description', 'description', 'More about this property', source.property.additionalDescription, 'offer.overview.additional-description', 'page:overview', 50)
      // PropertyNavigableOfferContent uses `structuredFacilities` for the facilities rework UI and
      // otherwise falls back to legacy `property.facilities` markdown, then amenity groups.
      addTextAtom(atoms, 'facilities:root:content', 'facilities', 'Facilities', getPropertyFacilitiesSnapshotText(source.property), 'offer.property.facilities', 'facilities:root', 60)
      addTextAtom(atoms, 'location:root:description', 'location', 'Location details', source.property.locationDescription, 'offer.location.description', 'location:root', 60)
      break
    default:
      assertUnreachable(source)
  }

  const referencedTargetIds = new Set([
    ...atoms.map(atom => atom.targetId),
    ...atoms.map(atom => atom.parentTargetId),
  ])

  return {
    atoms,
    targets: targets.filter(target => target.targetId && referencedTargetIds.has(target.targetId)),
  }
}

/**
 * Sanitises `getId(item)` once per item and drops items whose id sanitises to
 * empty, so callers never need a second `toOfferPageSnapshotId` call (and
 * never need an `as string` cast) to recover the id `uniqueBy` already saw.
 *
 * `packageSnapshot`'s `uniqueBy(withSnapshotId(items, getId), pair => pair.id)`
 * calls below have no collision-disambiguation, unlike the fine-print title
 * collisions handled in `offerPageFinePrintContext.ts` — deliberately, not an
 * oversight. Every id these calls sanitise is either a Salesforce record id
 * (flash package/rate ids), a UUID (bedbank/accommodation-service room/rate
 * ids), or an internally-generated slug (`room-N`), all already fully inside
 * `toOfferPageSnapshotId`'s safe charset, so sanitisation is a no-op for every
 * real id source today — unlike fine-print titles, which are genuine free
 * text. If an upstream service ever emitted a non-UUID/non-alphanumeric id in
 * one of these fields, checkout/cart flows that already treat the same ids as
 * URL/DB-safe tokens would break first, well before this snapshot code path.
 */
function withSnapshotId<T>(items: Array<T>, getId: (item: T) => string | undefined): Array<{ item: T; id: string }> {
  return items.flatMap(item => {
    const id = toOfferPageSnapshotId(getId(item))
    return id ? [{ item, id }] : []
  })
}

/**
 * Resolves which already-built package/rate target a multi-room booking's
 * room slot maps to, so its selection atom can link to real inventory
 * instead of duplicating that inventory's own content. Returns undefined
 * (dropping the room's atom entirely) rather than a dangling reference when
 * the room slot's roomId/rateId don't resolve to anything actually built —
 * e.g. no room/rate chosen yet, or stale ids from a data inconsistency.
 */
function resolveRoomSlotTargetId(
  source: OfferPageAssistantSource,
  room: OfferPageSnapshotRoom,
  targetIds: Set<string>,
): string | undefined {
  if (source.variant === 'flash') {
    if (!room.roomId || !room.rateId) {
      return undefined
    }

    const uniqueKey = getPackageUniqueKey(room.roomId, source.selection.duration, room.rateId)
    const packageView = source.packageViews.find(view => view.package.uniqueKey === uniqueKey)
    const targetId = packageView ? `package:${toOfferPageSnapshotId(packageView.package.uniqueKey)}` : undefined
    return targetId && targetIds.has(targetId) ? targetId : undefined
  }

  const roomId = toOfferPageSnapshotId(room.roomId)
  if (!roomId) {
    return undefined
  }

  const rateId = toOfferPageSnapshotId(room.rateId)
  const rateTargetId = rateId ? `room-package:${roomId}:${rateId}` : undefined
  if (rateTargetId && targetIds.has(rateTargetId)) {
    return rateTargetId
  }

  const roomTargetId = `package:${roomId}`
  return targetIds.has(roomTargetId) ? roomTargetId : undefined
}

function packageSnapshot(source: OfferPageAssistantSource): {
  atoms: Array<SurfaceAtomRequest>
  targets: Array<SurfaceTargetRequest>
} {
  const atoms: Array<SurfaceAtomRequest> = []
  const targets: Array<SurfaceTargetRequest> = []
  const addPackage = (
    id: string,
    roomName: string | undefined,
    packageName: string | undefined,
    description: string | undefined,
    rankHint: number,
  ) => {
    const targetId = `package:${id}`
    targets.push(createTarget(targetId, 'package', packageName ?? roomName ?? 'Package', `offer.packages.${id}`, {
      dataClassification: 'commercial_surface',
    }))
    addTextAtom(atoms, `${targetId}:room`, 'room', 'Room', roomName, `offer.packages.${id}.room`, targetId, rankHint, 'commercial_surface')
    addTextAtom(atoms, `${targetId}:summary`, 'package', 'Package', packageName, `offer.packages.${id}.summary`, targetId, rankHint + 1, 'commercial_surface')
    addTextAtom(atoms, `${targetId}:description`, 'package', 'Package details', description, `offer.packages.${id}.description`, targetId, rankHint + 2, 'commercial_surface')
  }
  const addRate = (
    roomId: string,
    rateId: string,
    rateLabel: string | undefined,
  ): string => {
    const roomTargetId = `package:${roomId}`
    const rateTargetId = `room-package:${roomId}:${rateId}`
    targets.push(createTarget(rateTargetId, 'rate', rateLabel ?? 'Rate', `offer.packages.${roomId}.rates.${rateId}`, {
      dataClassification: 'commercial_surface',
      parentTargetId: roomTargetId,
    }))
    return rateTargetId
  }

  switch (source.variant) {
    case 'flash':
      uniqueBy(
        withSnapshotId(source.packageViews, packageView => packageView.package.uniqueKey),
        pair => pair.id,
      ).forEach(({ item: packageView, id }, index) => {
        addPackage(
          id,
          packageView.package.roomType.name,
          packageView.package.name,
          packageView.package.description,
          index * 10,
        )
      })
      break
    case 'bedbank':
      uniqueBy(
        withSnapshotId(source.packageViews, packageView => packageView.package.roomId),
        pair => pair.id,
      ).forEach(({ item: packageView, id: roomId }, index) => {
        addPackage(roomId, packageView.package.name, undefined, packageView.package.description, index * 10)
        uniqueBy(
          withSnapshotId(packageView.rates, rate => rate.id),
          pair => pair.id,
        ).forEach(({ item: rate, id: rateId }, rateIndex) => {
          const rateTargetId = addRate(roomId, rateId, rate.boardCode)
          addTextAtom(atoms, `${rateTargetId}:board`, 'rate', 'Meal plan', rate.boardCode, `offer.packages.${roomId}.rates.${rateId}.board`, rateTargetId, index * 10 + rateIndex + 5, 'commercial_surface')
          addTextAtom(atoms, `${rateTargetId}:cancellation`, 'rate', 'Cancellation policy', rate.refundable ? 'Refundable' : 'Non-refundable', `offer.packages.${roomId}.rates.${rateId}.cancellation`, rateTargetId, index * 10 + rateIndex + 6, 'commercial_surface')
        })
      })
      break
    case 'lme_property':
    case 'spp_property':
      uniqueBy(
        withSnapshotId(source.roomRateViews, roomRateView => roomRateView.roomId),
        pair => pair.id,
      ).forEach(({ item: roomRateView, id: roomId }, roomIndex) => {
        const roomTargetId = `package:${roomId}`
        targets.push(createTarget(roomTargetId, 'room', roomRateView.propertyRoom.name, `offer.packages.${roomId}`, {
          dataClassification: 'commercial_surface',
        }))
        addTextAtom(atoms, `${roomTargetId}:summary`, 'room', 'Room', roomRateView.propertyRoom.name, `offer.packages.${roomId}.summary`, roomTargetId, roomIndex * 100, 'commercial_surface')
        addTextAtom(atoms, `${roomTargetId}:description`, 'room', 'Room details', roomRateView.propertyRoom.description, `offer.packages.${roomId}.description`, roomTargetId, roomIndex * 100 + 1, 'commercial_surface')
        uniqueBy(
          withSnapshotId(roomRateView.rates, rate => rate.rateId),
          pair => pair.id,
        ).forEach(({ item: rate, id: rateId }, rateIndex) => {
          const rateTargetId = addRate(roomId, rateId, rate.name)
          addTextAtom(atoms, `${rateTargetId}:summary`, 'rate', 'Rate', rate.name, `offer.packages.${roomId}.rates.${rateId}.summary`, rateTargetId, roomIndex * 100 + rateIndex + 10, 'commercial_surface')
        })
      })
      break
    default:
      assertUnreachable(source)
  }

  if (source.selection.rooms.length > 1) {
    const targetIds = new Set(nonNullable(targets.map(target => target.targetId)))
    withSnapshotId(source.selection.rooms, room => room.id).forEach(({ item: room, id: roomSlotId }, index) => {
      const resolvedTargetId = resolveRoomSlotTargetId(source, room, targetIds)
      const occupancyText = resolvedTargetId ? formatAssistantRoomOccupancyText(room) : undefined
      if (!resolvedTargetId || !occupancyText) {
        return
      }

      const atom = createAtom(
        `booking:room:${roomSlotId}:selection`,
        'booking_selection',
        'Room selection',
        formatAssistantRoomLabel(index + 1, occupancyText),
        `offer.booking.rooms.${roomSlotId}.selection`,
        resolvedTargetId,
        1000 + index,
        'commercial_surface',
      )
      if (atom) {
        atoms.push(atom)
      }
    })
  }

  return { atoms, targets }
}

export function ratesStatusFact(isLoading: boolean, hasError: boolean): 'loading' | 'unavailable' | undefined {
  if (isLoading) {
    return 'loading'
  }

  if (hasError) {
    return 'unavailable'
  }

  return undefined
}

function packageAvailabilityFacts(source: OfferPageAssistantSource): Array<SurfaceManifestFactRequest> {
  if (source.variant !== 'lme_property' && source.variant !== 'spp_property') {
    return []
  }

  return nonNullable([
    createFact('packages.rates_status', source.ratesStatus, 'commercial_surface'),
  ])
}

function policySnapshotSources(source: OfferPageAssistantSource): {
  bedbankOffer?: App.BedbankOffer
  hotelOffer?: App.HotelOffer
  property?: App.Property.Property
} {
  switch (source.variant) {
    case 'flash':
      return { hotelOffer: source.offer }
    case 'bedbank':
      return { bedbankOffer: source.offer }
    case 'lme_property':
    case 'spp_property':
      return { property: source.property }
    default:
      return assertUnreachable(source)
  }
}

/**
 * Fine-print section titles can collide (a bedbank offer's hardcoded section
 * titles and its arbitrary supplier-controlled policy keys are merged with no
 * upstream dedupe). Breaking the collision with a deterministic suffix keeps
 * both sections addressable under distinct target ids instead of silently
 * losing one to a duplicate targetId — the ADR requires canonical construction
 * to never drop safe, distinct rendered content.
 */
function uniqueFinePrintSectionKey(baseKey: string, usedKeys: Set<string>): string {
  if (!usedKeys.has(baseKey)) {
    return baseKey
  }

  let suffix = 2
  while (usedKeys.has(`${baseKey}-${suffix}`)) {
    suffix += 1
  }
  return `${baseKey}-${suffix}`
}

function policySnapshot(source: OfferPageAssistantSource): {
  atoms: Array<SurfaceAtomRequest>
  targets: Array<SurfaceTargetRequest>
} {
  const sections = buildOfferPageFinePrintSectionsFromSources(policySnapshotSources(source), { normalization: 'snapshot' })
  const atoms: Array<SurfaceAtomRequest> = []
  const targets: Array<SurfaceTargetRequest> = sections.length ? [
    createTarget('fine-print:root', 'section', 'Things to know', 'offer.policies', {
      dataClassification: 'commercial_surface',
      linkStability: 'same_scope_current_target',
      modelUse: 'auto_focus_allowed',
    }),
  ] : []

  const usedSectionKeys = new Set<string>()
  for (const [index, section] of sections.entries()) {
    const baseSectionKey = toOfferPageSnapshotId(
      section.title?.toLowerCase()?.replace(/\s+/g, '-') ?? `section-${index + 1}`,
    )
    if (!baseSectionKey) {
      continue
    }
    const sectionKey = uniqueFinePrintSectionKey(baseSectionKey, usedSectionKeys)
    usedSectionKeys.add(sectionKey)
    const targetId = `fine-print:${sectionKey}`
    targets.push(createTarget(targetId, 'policy', section.title ?? 'Policy', `offer.policies.${sectionKey}`, {
      dataClassification: 'commercial_surface',
      parentTargetId: 'fine-print:root',
    }))
    addTextAtom(atoms, `${targetId}:content`, 'policy', section.title ?? 'Policy', section.body, `offer.policies.${sectionKey}.content`, targetId, index, 'commercial_surface')
  }

  return { atoms, targets }
}

function addMissingAtomTargets(
  atoms: Array<SurfaceAtomRequest>,
  existingTargets: Array<SurfaceTargetRequest>,
): Array<SurfaceTargetRequest> {
  const targetIds = new Set(existingTargets.map(target => target.targetId))
  const targets = [...existingTargets]

  for (const atom of atoms) {
    if (!(atom.targetId && atom.label && atom.sourcePath) || targetIds.has(atom.targetId)) {
      continue
    }

    targets.push(createTarget(atom.targetId, atom.kind ?? 'content', atom.label, atom.sourcePath, {
      dataClassification: atom.dataClassification,
      parentTargetId: atom.parentTargetId ?? undefined,
    }))
    targetIds.add(atom.targetId)
  }

  return sortTargets(targets)
}

function belongsToSourcePathNamespace(sourcePath: string | null | undefined, root: string): boolean {
  return sourcePath === root || sourcePath?.startsWith(`${root}.`) === true
}

function belongsToPoliciesChunk(sourcePath: string | null | undefined): boolean {
  return belongsToSourcePathNamespace(sourcePath, 'offer.policies')
}

function belongsToPropertyChunk(sourcePath: string | null | undefined): boolean {
  return belongsToSourcePathNamespace(sourcePath, 'offer.overview') ||
    belongsToSourcePathNamespace(sourcePath, 'offer.location') ||
    sourcePath?.startsWith('offer.property.') === true
}

function buildAtomChunk(chunkId: OfferChunkId, atoms: Array<SurfaceAtomRequest>): AssistantSurfaceSnapshotChunk {
  return {
    chunkId,
    kind: 'atoms',
    contentHash: assistantSurfaceSnapshotChunkContentHash({ kind: 'atoms', atoms }),
    atoms,
  }
}

function buildTargetChunk(chunkId: OfferChunkId, targets: Array<SurfaceTargetRequest>): AssistantSurfaceSnapshotChunk {
  return {
    chunkId,
    kind: 'targets',
    contentHash: assistantSurfaceSnapshotChunkContentHash({ kind: 'targets', targets }),
    targets,
  }
}

function buildCommittedStateChunk(
  committedState: Array<SurfaceRecentCommittedStateRequest>,
): AssistantSurfaceSnapshotChunk {
  return {
    chunkId: 'committed',
    kind: 'recent_committed_state',
    contentHash: assistantSurfaceSnapshotChunkContentHash({
      kind: 'recent_committed_state',
      recentCommittedState: committedState,
    }),
    recentCommittedState: committedState,
  }
}

function buildCapabilityChunk(): AssistantSurfaceSnapshotChunk {
  return {
    chunkId: 'offer',
    kind: 'capabilities',
    contentHash: assistantSurfaceSnapshotChunkContentHash({ kind: 'capabilities', capabilities }),
    capabilities,
  }
}

function chunkRef(chunk: AssistantSurfaceSnapshotChunk): AssistantSurfaceSnapshotChunkRef {
  switch (chunk.kind) {
    case 'atoms':
      return { chunkId: chunk.chunkId, contentHash: chunk.contentHash, kind: chunk.kind, itemCount: chunk.atoms.length }
    case 'targets':
      return { chunkId: chunk.chunkId, contentHash: chunk.contentHash, kind: chunk.kind, itemCount: chunk.targets.length }
    case 'recent_committed_state':
      return { chunkId: chunk.chunkId, contentHash: chunk.contentHash, kind: chunk.kind, itemCount: chunk.recentCommittedState.length }
    case 'capabilities':
      return { chunkId: chunk.chunkId, contentHash: chunk.contentHash, kind: chunk.kind, itemCount: chunk.capabilities.length }
  }
}

function normaliseContributionAtoms(contributions: Array<OfferPageSnapshotContribution>): Array<SurfaceAtomRequest> {
  return contributions
    .flatMap(contribution => contribution.atoms.flatMap(atom => {
      const atomId = atom.atomId ? toOfferPageSnapshotId(atom.atomId) : undefined
      const targetId = atom.targetId ? toOfferPageSnapshotId(atom.targetId) : undefined
      const parentTargetId = atom.parentTargetId ? toOfferPageSnapshotId(atom.parentTargetId) : undefined
      const label = atom.label ? toOfferPageSnapshotLabel(atom.label) : undefined
      const sourcePath = atom.sourcePath ? toOfferPageSnapshotSourcePath(atom.sourcePath) : undefined
      if (!atomId || !targetId || !label || !sourcePath) {
        return []
      }

      const segments = splitOfferPageSnapshotAtomText(atom.displayText)
      return segments.map((displayText, index) => {
        const segmentSuffix = segments.length > 1 ? `:seg-${index + 1}` : ''
        return {
          ...atom,
          atomId: `${atomId}${segmentSuffix}`,
          targetId,
          ...(parentTargetId ? { parentTargetId } : {}),
          label,
          displayText,
          sourcePath: segmentSuffix ? `${sourcePath}${segmentSuffix}` : sourcePath,
          rankHint: (atom.rankHint ?? 0) + index,
        }
      })
    }))
    .filter(atom => atom.atomId && atom.label && atom.displayText && atom.sourcePath)
}

function normaliseContributionCommittedState(
  contributions: Array<OfferPageSnapshotContribution>,
): Array<SurfaceRecentCommittedStateRequest> {
  return contributions
    .flatMap(contribution => contribution.recentCommittedState)
    .flatMap(state => {
      const key = state.key ? toOfferPageSnapshotId(state.key) : undefined
      const targetId = state.targetId ? toOfferPageSnapshotId(state.targetId) : undefined
      const label = state.label ? toOfferPageSnapshotLabel(state.label) : undefined
      const sourcePath = state.sourcePath ? toOfferPageSnapshotSourcePath(state.sourcePath) : undefined
      if (!key || !state.kind || !label || !sourcePath) {
        return []
      }

      const segments = splitOfferPageSnapshotAtomText(state.displayText)
      return segments.map((displayText, index) => {
        const segmentSuffix = segments.length > 1 ? `:seg-${index + 1}` : ''
        return {
          ...state,
          key: `${key}${segmentSuffix}`,
          ...(targetId ? { targetId } : {}),
          label,
          displayText,
          sourcePath: `${sourcePath}${segmentSuffix}`,
        }
      })
    })
    .filter(state => state.key && state.kind && state.label && state.displayText && state.sourcePath)
}

/**
 * The 5 content chunk namespaces (booking/packages/policies/property/reviews)
 * are matched by sourcePath prefix with no catch-all bucket, and the 12 chunk
 * identities are immutable within schemaVersion 1 — there is no "misc" chunk
 * to add one. If a future atom/target's sourcePath doesn't match any
 * namespace, it would otherwise silently vanish from every chunk. This is a
 * low-cardinality count check only — no atom/target prose is logged.
 */
function warnIfChunkCoverageIncomplete(
  atoms: Array<SurfaceAtomRequest>,
  targets: Array<SurfaceTargetRequest>,
  chunks: Array<AssistantSurfaceSnapshotChunk>,
): void {
  const chunkedAtomCount = chunks.reduce((total, chunk) => total + (chunk.kind === 'atoms' ? chunk.atoms.length : 0), 0)
  const chunkedTargetCount = chunks.reduce((total, chunk) => total + (chunk.kind === 'targets' ? chunk.targets.length : 0), 0)
  if (chunkedAtomCount !== atoms.length || chunkedTargetCount !== targets.length) {
    reportLog('[OfferPageSnapshot] atom/target count did not match total chunked count', {
      totalAtoms: atoms.length,
      chunkedAtoms: chunkedAtomCount,
      totalTargets: targets.length,
      chunkedTargets: chunkedTargetCount,
    })
  }
}

export function buildOfferPageSnapshot(input: OfferPageSnapshotInput): OfferPageSnapshotBuildResult | undefined {
  const surfaceContextScopeKey = buildOfferPageSnapshotScopeKeyFromSource(input.source)
  if (!surfaceContextScopeKey) {
    return undefined
  }

  const ageThresholds = getAssistantOccupancyAgeThresholdsFromSnapshotSource(input.source)
  const booking = selectionSnapshot(input.source.selection, ageThresholds)
  const overview = overviewSnapshot(input.source)
  const staticContent = staticContentSnapshot(input.source)
  const packages = packageSnapshot(input.source)
  const policies = policySnapshot(input.source)
  const contributionAtoms = normaliseContributionAtoms(input.contributions)
  const contributionCommitted = normaliseContributionCommittedState(input.contributions)
  // Contributions (unlike the fixed builder sections above) come from independent,
  // uncoordinated call sites — e.g. a review visible in both the inline section and
  // an open drawer would otherwise emit the same atomId twice. The ADR requires
  // duplicate atom IDs be rejected; keep the first deterministic one instead of
  // letting the backend reject the whole snapshot with duplicate_atom_id.
  const atoms = sortAtoms(uniqueBy([
    ...booking.atoms,
    ...overview.atoms,
    ...staticContent.atoms,
    ...packages.atoms,
    ...policies.atoms,
    ...contributionAtoms,
  ], atom => atom.atomId))
  const targets = addMissingAtomTargets(atoms, [
    ...booking.targets,
    ...overview.targets,
    ...staticContent.targets,
    ...packages.targets,
    ...policies.targets,
  ])
  const recentCommittedState = sortCommittedState([...booking.committed, ...contributionCommitted])
  const manifestFacts = sortManifestFacts([...booking.facts, ...overview.facts, ...packageAvailabilityFacts(input.source)])
  const visibleState = input.contributions.reduce<{
    activeControl?: string
    activeDrawer?: string
    activeModal?: string
    suppressAutoFocus: boolean
  }>((state, contribution) => ({
    activeControl: contribution.visibleState?.activeControl ?? state.activeControl,
    activeDrawer: contribution.visibleState?.activeDrawer ?? state.activeDrawer,
    activeModal: contribution.visibleState?.activeModal ?? state.activeModal,
    suppressAutoFocus: state.suppressAutoFocus || contribution.visibleState?.suppressAutoFocus === true,
  }), {
    suppressAutoFocus: false,
  })
  const chunks = [
    buildCommittedStateChunk(recentCommittedState),
    buildAtomChunk('booking', atoms.filter(atom => atom.sourcePath?.startsWith('offer.booking.'))),
    buildTargetChunk('booking', targets.filter(target => target.sourcePath?.startsWith('offer.booking.'))),
    buildAtomChunk('packages', atoms.filter(atom => atom.sourcePath?.startsWith('offer.packages.'))),
    buildTargetChunk('packages', targets.filter(target => target.sourcePath?.startsWith('offer.packages.'))),
    buildAtomChunk('policies', atoms.filter(atom => belongsToPoliciesChunk(atom.sourcePath))),
    buildTargetChunk('policies', targets.filter(target => belongsToPoliciesChunk(target.sourcePath))),
    buildAtomChunk('property', atoms.filter(atom => belongsToPropertyChunk(atom.sourcePath))),
    buildTargetChunk('property', targets.filter(target => belongsToPropertyChunk(target.sourcePath))),
    buildAtomChunk('reviews', atoms.filter(atom => atom.sourcePath?.startsWith('offer.reviews.'))),
    buildTargetChunk('reviews', targets.filter(target => target.sourcePath?.startsWith('offer.reviews.'))),
    buildCapabilityChunk(),
  ]
  warnIfChunkCoverageIncomplete(atoms, targets, chunks)
  const manifest: AssistantSurfaceSnapshotChunkManifest = {
    surface: 'accommodation_offer_page',
    schemaVersion: 1,
    surfaceContextScopeKey,
    ...(input.source.pageUrl ? { pageUrl: input.source.pageUrl } : {}),
    visibleState: {
      ...visibleState,
      manifestFacts,
    },
    chunks: chunks.map(chunkRef),
  }
  const snapshot: AssistantSurfaceSnapshotRequest = {
    surface: 'accommodation_offer_page',
    schemaVersion: 1,
    surfaceContextScopeKey,
    ...(input.source.pageUrl ? { pageUrl: input.source.pageUrl } : {}),
    visibleState: manifest.visibleState,
    recentCommittedState,
    atoms,
    targets,
    capabilities,
  }

  return { chunks, manifest, snapshot }
}
